import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  TextField,
  alpha,
} from "@mui/material";
import { Phone as PhoneIcon, Public as PublicIcon } from "@mui/icons-material";
import { UpdateGfStoreDto } from "@swagger/typescript-fetch-goflyer";
import { FieldPath, FieldValue } from "../utils/fieldChangeHelpers";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

interface ContactReviewsProps {
  store?: UpdateGfStoreDto;
  isEditing: boolean;
  onFieldChange: (field: FieldPath, value: FieldValue) => void;
}

export const ContactReviews: React.FC<ContactReviewsProps> = ({
  store,
  isEditing,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      elevation={1}
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <PhoneIcon sx={{ color: "#f58321", mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {t(messages.Contact_Reviews())}
          </Typography>
        </Box>

        <List dense>
          <ListItem disablePadding sx={{ mb: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PhoneIcon sx={{ color: "#02b7cf" }} />
            </ListItemIcon>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.Phone_Number())}
                value={store?.phone || ""}
                onChange={(e) => onFieldChange("phone", e.target.value)}
                variant="outlined"
                size="small"
                placeholder={t(messages.Enter_Phone_Number())}
              />
            ) : (
              <ListItemText
                primary={t(messages.Phone_Number())}
                secondary={store?.phone || t(messages.Not_Available())}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              />
            )}
          </ListItem>
          <ListItem disablePadding>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PublicIcon sx={{ color: "#02b7cf" }} />
            </ListItemIcon>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.Google_Maps_URL())}
                value={store?.googleMapUrl || ""}
                onChange={(e) => onFieldChange("googleMapUrl", e.target.value)}
                variant="outlined"
                size="small"
                placeholder="https://maps.google.com/..."
              />
            ) : (
              <ListItemText
                primary={t(messages.Google_Maps())}
                secondary={
                  store?.googleMapUrl ? (
                    <Button
                      component="a"
                      variant="text"
                      size="small"
                      href={store?.googleMapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        p: 0,
                        justifyContent: "flex-start",
                        textTransform: "none",
                      }}
                    >
                      {t(messages.View_On_Google_Maps())}
                    </Button>
                  ) : (
                    t(messages.Not_Available())
                  )
                }
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
              />
            )}
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
};
